import { useState } from "react";
import { useApi, mutar } from "../../lib/useApi";
import { Card, Button, Input, Chip, SectionLabel, EmptyState, useToast, cn } from "../../components/ui";
import { PanelLateral, BloquePanel } from "../../components/ui/PanelLateral";
import type { Rol, SesionActiva } from "../../lib/tipos";

// Usuarios y roles (Ajustes › detalle). Cada fila dice quién es y su estado; UN botón «Editar» abre el panel
// lateral con datos, rol, contraseña y desactivar (Kevin, 10-oct-2026, research
// `docs/research/2026-10-10-botones-por-fila`). Scoping duro en el server: el Dueño cambia todo de todos; el
// Encargado, nombre, contraseña y estado de los vendedores de SU botica. En la fila propia solo van los datos:
// el rol, la contraseña sin pedir la actual y el desactivar dejarían a la persona fuera (tramo 08).

type Usuario = { id: string; nombre: string; email: string; rol: string; sucursal_id: string | null; activo: number };
type Sucursal = { id: string; nombre: string };

const SELECT_CLS = "min-h-[44px] w-full rounded-[9px] border border-line-input bg-field px-3 text-[13px] text-ink outline-none disabled:opacity-50";

// Nombre del rol tal como lo dice la familia (Kevin, 10-oct-2026: «Dueño de la cadena», «Encargado de botica»).
const ROL_LABEL: Record<string, string> = {
  super_admin: "Dueño de la cadena",
  admin_sucursal: "Encargado de botica",
  operador: "Vendedor",
  lector_reportes: "Lector",
};

const ROLES: { rol: Rol; que: string }[] = [
  { rol: "super_admin", que: "Ve y cambia las 3 boticas, las cuentas y los precios." },
  { rol: "admin_sucursal", que: "Maneja su botica: ventas, stock, caja y sus vendedores." },
  { rol: "operador", que: "Atiende en el mostrador de su botica." },
  { rol: "lector_reportes", que: "Solo mira las cifras de su botica; no cambia nada." },
];

export function Usuarios({ sesion }: { sesion: SesionActiva }) {
  const esSuper = sesion.usuario.rol === "super_admin";
  const lista = useApi<{ usuarios: Usuario[] }>("/usuarios");
  const sucursales = useApi<{ sucursales: Sucursal[] }>(esSuper ? "/sucursales" : null);
  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<Usuario | null>(null);
  const toast = useToast();

  const sucNombre = (id: string | null): string | null =>
    sucursales.data?.sucursales.find((s) => s.id === id)?.nombre ?? (sesion.sucursal && sesion.sucursal.id === id ? sesion.sucursal.nombre : null);
  const rolAlcance = (u: Usuario): string => {
    if (u.rol === "super_admin") return ROL_LABEL.super_admin!;
    const botica = sucNombre(u.sucursal_id);
    if (u.rol === "admin_sucursal") return botica ? `Encargado de botica ${botica}` : "Encargado de botica";
    return `${ROL_LABEL[u.rol] ?? u.rol}${botica ? ` · ${botica}` : ""}`;
  };

  const items = lista.data?.usuarios ?? [];

  return (
    <div className="flex max-w-[820px] flex-col gap-4">
      {/* Tramo 07 (O-11): en el celular el texto y el botón bajan de renglón en vez de apretarse. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="min-w-[220px] flex-1 text-[13px] text-ink-2">
          Quién entra al panel y qué puede hacer. El dueño ve toda la cadena; el encargado, su botica.
          {/* 8-oct-2026: Kevin, entrando como Encargado, leyó la lista corta como «solo hay una cuenta». */}
          {!esSuper && sesion.sucursal && ` Aquí ves solo las cuentas de ${sesion.sucursal.nombre}; las de las otras boticas las ve el Dueño.`}
        </p>
        <Button variant={creando ? "outline" : "primary"} size="sm" onClick={() => setCreando((v) => !v)} className="min-h-[44px] whitespace-nowrap">
          {creando ? "Cerrar" : "Agregar usuario"}
        </Button>
      </div>

      {creando && (
        <CrearUsuario
          esSuper={esSuper}
          sucursales={sucursales.data?.sucursales ?? []}
          onListo={() => {
            setCreando(false);
            lista.recargar();
            toast("Usuario creado.");
          }}
        />
      )}

      <Card>
        {lista.cargando ? (
          <p className="py-4 text-center text-[13px] text-ink-3">Cargando usuarios…</p>
        ) : lista.error ? (
          <div className="py-4 text-center">
            <p className="text-[13px] text-accent-ink">{lista.error}</p>
            <button onClick={lista.recargar} className="mt-2 text-[12.5px] text-link underline">Reintentar</button>
          </div>
        ) : items.length === 0 ? (
          <EmptyState title="Sin usuarios" subtitle="Agrega el primero con “Agregar usuario”." />
        ) : (
          <ul className="flex flex-col">
            {items.map((u) => {
              const esYo = u.id === sesion.usuario.id;
              // RC-10: el encargado solo gestiona a los vendedores de SU botica. El dueño, a todos.
              const puede = esSuper || (u.rol === "operador" && u.sucursal_id === sesion.usuario.sucursalId);
              return (
                <li key={u.id} className="flex flex-col gap-2 border-b border-line-row py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <p className={cn("truncate text-[13px] font-semibold", u.activo !== 1 ? "text-ink-3" : "text-ink")}>
                        {u.nombre}
                        {esYo && <span className="font-normal text-ink-3"> (tú)</span>}
                      </p>
                      <Chip variant={u.activo === 1 ? "ok" : "neutral"}>{u.activo === 1 ? "Activo" : "Inactivo"}</Chip>
                    </div>
                    <p className="truncate text-[12px] text-ink-2">{u.email}</p>
                    <p className="truncate text-[12px] text-ink-2">{rolAlcance(u)}</p>
                  </div>
                  {puede && (
                    <div className="flex shrink-0 flex-col gap-1 sm:items-end">
                      <Button variant="outline" size="sm" onClick={() => setEditando(u)} className="min-h-[44px] self-start sm:min-h-[34px] sm:self-end">
                        Editar
                      </Button>
                      {/* Tramo 08: la contraseña propia se cambia en «Mi contraseña», que pide la actual. */}
                      {esYo && <p className="text-[12px] text-ink-3 sm:text-right">Tu contraseña se cambia en «Mi contraseña».</p>}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {editando && (
        <EditarUsuario
          key={editando.id}
          u={editando}
          esYo={editando.id === sesion.usuario.id}
          esSuper={esSuper}
          sucursales={sucursales.data?.sucursales ?? []}
          alcance={rolAlcance(editando)}
          onCerrar={() => setEditando(null)}
          onCambio={(msg) => {
            toast(msg);
            lista.recargar();
          }}
        />
      )}
    </div>
  );
}

function EditarUsuario({
  u,
  esYo,
  esSuper,
  sucursales,
  alcance,
  onCerrar,
  onCambio,
}: {
  u: Usuario;
  esYo: boolean;
  esSuper: boolean;
  sucursales: Sucursal[];
  alcance: string;
  onCerrar: () => void;
  onCambio: (mensaje: string) => void;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [nombre, setNombre] = useState(u.nombre);
  const [email, setEmail] = useState(u.email);
  const [rol, setRol] = useState<Rol>(u.rol as Rol);
  const [botica, setBotica] = useState(u.sucursal_id ?? "");
  const [errorDatos, setErrorDatos] = useState<string | null>(null);
  const [clave, setClave] = useState("");
  const [errorClave, setErrorClave] = useState<string | null>(null);
  const [confirmarBaja, setConfirmarBaja] = useState(false);

  const cambiaRol = esSuper && !esYo;
  const emailNuevo = email.trim().toLowerCase() !== u.email;

  async function guardar() {
    if (!nombre.trim()) {
      setErrorDatos("Escribe el nombre.");
      return;
    }
    if (cambiaRol && rol !== "super_admin" && !botica) {
      setErrorDatos("Elige la botica de esta persona.");
      return;
    }
    const cuerpo: Record<string, unknown> = { nombre: nombre.trim() };
    if (esSuper && emailNuevo) cuerpo.email = email.trim();
    if (cambiaRol && (rol !== u.rol || (rol !== "super_admin" && botica !== u.sucursal_id))) {
      cuerpo.rol = rol;
      cuerpo.sucursal_id = rol === "super_admin" ? null : botica;
    }
    setOcupado(true);
    setErrorDatos(null);
    try {
      await mutar(`/usuarios/${u.id}`, { method: "PATCH", body: cuerpo });
      onCambio(cuerpo.email ? `Listo. Desde ahora ${nombre.trim()} entra con ${email.trim().toLowerCase()}.` : "Cambios guardados.");
      onCerrar();
    } catch (e) {
      setErrorDatos(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  async function ponerClave() {
    if (clave.length < 8) {
      setErrorClave("Mínimo 8 caracteres.");
      return;
    }
    setOcupado(true);
    setErrorClave(null);
    try {
      await mutar(`/usuarios/${u.id}`, { method: "PATCH", body: { password: clave } });
      setClave("");
      onCambio(`Contraseña de ${u.nombre} actualizada.`);
    } catch (e) {
      setErrorClave(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  async function cambiarEstado() {
    setOcupado(true);
    try {
      await mutar(`/usuarios/${u.id}`, { method: "PATCH", body: { activo: u.activo !== 1 } });
      onCambio(u.activo === 1 ? `${u.nombre} quedó inactivo.` : `${u.nombre} está activo.`);
      onCerrar();
    } finally {
      setOcupado(false);
    }
  }

  return (
    <PanelLateral titulo={`Editar a ${u.nombre}`} subtitulo={alcance} onCerrar={onCerrar}>
      <BloquePanel titulo="Datos">
        <label className="flex flex-col gap-1 text-[12px] text-ink-2">
          Nombre
          <Input value={nombre} onChange={(e) => setNombre(e.target.value)} aria-label={`Nombre de ${u.nombre}`} />
        </label>
        {esSuper && (
          <label className="flex flex-col gap-1 text-[12px] text-ink-2">
            Correo con el que entra
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label={`Correo de ${u.nombre}`} />
          </label>
        )}
        {esSuper && emailNuevo && (
          <p className="text-[12px] text-ink-2">Ojo: desde que guardes, {esYo ? "tú entras" : `${u.nombre} entra`} con el correo nuevo. La contraseña no cambia.</p>
        )}
      </BloquePanel>

      {cambiaRol && (
        <BloquePanel titulo="Rol">
          <div role="radiogroup" aria-label={`Rol de ${u.nombre}`} className="flex flex-col gap-1.5">
            {ROLES.map((r) => (
              <label
                key={r.rol}
                className={cn(
                  "flex cursor-pointer gap-2.5 rounded-[10px] border px-3 py-2.5",
                  rol === r.rol ? "border-accent bg-accent-soft/40" : "border-line-input hover:bg-hover-btn",
                )}
              >
                <input type="radio" name={`rol-${u.id}`} checked={rol === r.rol} onChange={() => setRol(r.rol)} className="mt-0.5" />
                <span>
                  <span className="block text-[13px] font-semibold text-ink">{ROL_LABEL[r.rol]}</span>
                  <span className="block text-[12px] text-ink-2">{r.que}</span>
                </span>
              </label>
            ))}
          </div>
          {rol !== "super_admin" && (
            <select value={botica} onChange={(e) => setBotica(e.target.value)} aria-label={`Botica de ${u.nombre}`} className={SELECT_CLS}>
              <option value="">— ¿En qué botica? —</option>
              {sucursales.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          )}
        </BloquePanel>
      )}

      <div className="flex flex-col gap-1.5">
        <Button onClick={() => void guardar()} disabled={ocupado} className="min-h-[44px] justify-center">
          {ocupado ? "Guardando…" : "Guardar cambios"}
        </Button>
        {errorDatos && <p className="text-[12px] text-accent-ink">{errorDatos}</p>}
      </div>

      {!esYo && (
        <BloquePanel titulo="Contraseña">
          <Input
            type="password"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            aria-label={`Contraseña nueva para ${u.nombre}`}
            placeholder="Contraseña nueva (mín. 8)"
          />
          <Button variant="outline" onClick={() => void ponerClave()} disabled={ocupado || !clave} className="min-h-[44px] justify-center">
            Poner esta contraseña
          </Button>
          {errorClave && <p className="text-[12px] text-accent-ink">{errorClave}</p>}
        </BloquePanel>
      )}

      {!esYo && (
        <BloquePanel titulo={u.activo === 1 ? "Desactivar" : "Activar"} peligro={u.activo === 1}>
          <p className="text-[12px] text-ink-2">
            {u.activo === 1 ? `${u.nombre} deja de poder entrar desde ese momento. Sus ventas y su historial se quedan.` : `${u.nombre} vuelve a poder entrar con su correo y su contraseña.`}
          </p>
          {u.activo === 1 && !confirmarBaja ? (
            <Button variant="outline" onClick={() => setConfirmarBaja(true)} disabled={ocupado} className="min-h-[44px] justify-center text-accent-ink">
              Desactivar cuenta
            </Button>
          ) : (
            <Button onClick={() => void cambiarEstado()} disabled={ocupado} className="min-h-[44px] justify-center">
              {u.activo === 1 ? "Sí, desactivar" : "Activar cuenta"}
            </Button>
          )}
        </BloquePanel>
      )}
    </PanelLateral>
  );
}

function CrearUsuario({ esSuper, sucursales, onListo }: { esSuper: boolean; sucursales: Sucursal[]; onListo: () => void }) {
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rol, setRol] = useState<Rol>("operador");
  const [sucursalId, setSucursalId] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function crear() {
    if (!nombre.trim() || !email.trim() || password.length < 8) {
      setError("Nombre, email y contraseña (mín. 8) requeridos.");
      return;
    }
    if (esSuper && rol !== "super_admin" && !sucursalId) {
      setError("Elige una botica.");
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await mutar("/usuarios", {
        method: "POST",
        body: { nombre: nombre.trim(), email: email.trim(), password, rol: esSuper ? rol : "operador", sucursal_id: esSuper ? sucursalId || null : undefined },
      });
      onListo();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setEnviando(false);
    }
  }

  const selectCls = "rounded-[9px] border border-line-input bg-field px-3 py-2.5 text-[13px] text-ink outline-none disabled:opacity-50";

  return (
    <Card className="gap-3">
      <SectionLabel>Nuevo usuario</SectionLabel>
      <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre" />
      <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" />
      <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Contraseña (mín. 8)" />
      {esSuper && (
        <div className="grid grid-cols-2 gap-2.5">
          <select value={rol} onChange={(e) => setRol(e.target.value as Rol)} className={selectCls}>
            <option value="operador">Vendedor</option>
            <option value="admin_sucursal">Encargado de botica</option>
            <option value="lector_reportes">Lector</option>
            <option value="super_admin">Dueño de la cadena</option>
          </select>
          <select value={sucursalId} onChange={(e) => setSucursalId(e.target.value)} disabled={rol === "super_admin"} className={selectCls}>
            <option value="">— Botica —</option>
            {sucursales.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>
      )}
      {error && <p className="text-[12.5px] text-accent-ink">{error}</p>}
      <div>
        <Button onClick={() => void crear()} disabled={enviando} className="max-md:min-h-[44px]">
          {enviando ? "Creando…" : "Crear usuario"}
        </Button>
      </div>
    </Card>
  );
}
